import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Building2, FileText, Shield, TrendingUp } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function CompanyFormation() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Building2,
      title: 'Business Structure',
      description: 'Choose the right entity type: LLC, Corporation, or Partnership.',
    },
    {
      icon: FileText,
      title: 'Legal Documentation',
      description: 'Complete preparation and filing of all required documents.',
    },
    {
      icon: Shield,
      title: 'Liability Protection',
      description: 'Separate personal assets from business liabilities.',
    },
    {
      icon: TrendingUp,
      title: 'Tax Benefits',
      description: 'Optimize your tax structure for maximum benefits.',
    },
  ];

  const entityTypes = [
    {
      type: 'Limited Liability Company (LLC)',
      description: 'Flexible structure combining liability protection with pass-through taxation. Ideal for small to medium businesses.',
      benefits: ['Personal liability protection', 'Pass-through taxation', 'Flexible management structure', 'Less paperwork than corporations'],
    },
    {
      type: 'C Corporation',
      description: 'Traditional corporate structure best for businesses seeking investment or planning to go public.',
      benefits: ['Unlimited shareholders', 'Easier to raise capital', 'Perpetual existence', 'Stock options for employees'],
    },
    {
      type: 'S Corporation',
      description: 'Corporation with pass-through taxation, combining benefits of C Corp structure with LLC tax treatment.',
      benefits: ['Pass-through taxation', 'Limited liability protection', 'No self-employment taxes on distributions', 'Enhanced credibility'],
    },
  ];

  const faqs = [
    {
      question: 'What is company formation?',
      answer: 'Company formation is the process of legally registering a business entity with the state. This creates a separate legal entity that can conduct business, enter contracts, and protect owners from personal liability.',
    },
    {
      question: 'Which business structure is right for me?',
      answer: 'The best structure depends on your business goals, size, industry, and tax situation. LLCs offer flexibility and simplicity, while corporations are better for raising capital. We can help you evaluate your options and choose the right structure.',
    },
    {
      question: 'How long does company formation take?',
      answer: 'The timeline varies by state, but typically takes 1-3 weeks. Expedited processing options are available in most states for faster formation.',
    },
    {
      question: 'What documents do I need?',
      answer: 'You will need to provide basic information including your company name, registered agent details, business address, and information about owners/members. We will guide you through the entire process and help prepare all necessary documents.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="company-formation-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="service-hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/90 via-[#06214c]/80 to-[#A0522D]/75\"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="service-hero-title"
            >
              Company Formation
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8" data-testid="service-hero-subtitle">
              Start your business the right way with professional company formation services. We handle the legal complexities so you can focus on growth.
            </p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary"
              data-testid="service-hero-cta"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-abstract-bg" data-testid="service-benefits-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Form a Company?
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Professional company formation provides essential benefits for your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in" data-testid={`benefit-${index}`}>
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Entity Types Section */}
      <section className="section bg-[#FFF5E6]" data-testid="service-entity-types-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Choose Your Business Structure
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Understanding different entity types helps you make the right choice for your business.
            </p>
          </div>

          <div className="space-y-6">
            {entityTypes.map((entity, index) => (
              <div key={index} className="card fade-in" data-testid={`entity-type-${index}`}>
                <h3 className="text-2xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  {entity.type}
                </h3>
                <p className="text-gray-600 mb-4">{entity.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {entity.benefits.map((benefit, bIndex) => (
                    <div key={bIndex} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-[#06214c] rounded-full"></div>
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section section-abstract-shapes" data-testid="service-process-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Our Formation Process
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Simple, streamlined steps to get your business up and running.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { step: 1, title: 'Consultation', description: 'We discuss your business needs and help you choose the right entity structure.' },
              { step: 2, title: 'Name Availability', description: 'We check your desired business name for availability and reserve it.' },
              { step: 3, title: 'Document Preparation', description: 'We prepare all necessary formation documents including articles of organization or incorporation.' },
              { step: 4, title: 'State Filing', description: 'We file your formation documents with the appropriate state agency.' },
              { step: 5, title: 'Completion', description: 'You receive your official formation documents and EIN, ready to conduct business.' },
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-4" data-testid={`process-step-${index}`}>
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#06214c] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-[#FFF5E6]" data-testid="service-faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Answers to common questions about company formation.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger
                    className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* CTA Section */}
      <section 
        className="section relative overflow-hidden" 
        data-testid="service-cta-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1496180470114-6ef490f3ff22?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 to-[#A0522D]/95"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Start Your Business?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white/90">
            Let us handle the legal complexities while you focus on building your business.
          </p>
          <button
            onClick={() => setShowDialog(true)}
            className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl"
            data-testid="service-cta-btn"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="company_formation" />

      <Footer />
    </div>
  );
}